import { ref, computed } from 'vue'
import { useUserStore } from '@zaohaowu/website-common/stores/userStore'

type GuideRouteName =
  | 'home'
  | 'account'
  | 'productDetail'
  | 'contentDetail'
  | 'seriesDetail'
  | 'generatorImage'
  | 'myHistory'

export const initGuideData = (pageName: GuideRouteName) => {
  const userStore = useUserStore()
  const routeName = pageName as GuideRouteName
  // home 对应页面 数组每个对象对应一个level 例如 p0, p1, p2 以及对应的步骤 steps 例如 0, 1, 2
  // type 0 新手引导 1 新功能引导
  const baseGuide: Record<GuideRouteName, { steps: { id: number; type: number }[] }[]> = {
    home: [
      {
        steps: [
          { id: 0, type: 0 },
          { id: 1, type: 0 },
          { id: 2, type: 0 },
          { id: 3, type: 0 },
          { id: 4, type: 0 }
        ]
      },
      { steps: [{ id: 5, type: 0 }] },
      { steps: [{ id: 6, type: 0 }] }
    ],
    account: [{ steps: [{ id: 16, type: 0 }] }],
    productDetail: [{ steps: [{ id: 9, type: 1 }] }],
    contentDetail: [
      {
        steps: [
          { id: 10, type: 1 },
          { id: 11, type: 1 },
          { id: 13, type: 0 },
          { id: 14, type: 0 }
        ]
      }
    ],
    seriesDetail: [{ steps: [{ id: 12, type: 1 }] }],
    generatorImage: [{ steps: [{ id: 7, type: 0 }] }],
    myHistory: [
      {
        steps: [
          { id: 8, type: 0 },
          { id: 17, type: 0 }
        ]
      }
    ]
  }

  const guide = ref<Record<GuideRouteName, { time: string; completeSteps: number[] }[]> | null>(
    null
  )
  const guideLevel = ref(0)
  const guideStep = ref<number[]>([])
  const getLocalGuideData = () => {
    const data = JSON.parse(localStorage.getItem('guide') as string)
    if (!data) return null
    return data
  }
  const setLocalGuideData = (data: any) => {
    localStorage.setItem('guide', JSON.stringify(data))
  }
  const setLocalAllComplete = () => {
    const now = new Date().toJSON().slice(0, 10)
    const data: Record<GuideRouteName, { time: string; completeSteps: number[] }[]> = {} as Record<
      GuideRouteName,
      { time: string; completeSteps: number[] }[]
    >

    Object.keys(baseGuide).forEach(key => {
      const routeKey = key as GuideRouteName
      data[routeKey] = baseGuide[routeKey].map(guideItem => ({
        time: now,
        completeSteps: [
          ...guideItem.steps
            .map(item => (item.type === 0 ? item.id : null))
            .filter((item): item is number => item !== null)
        ]
      }))
    })
    setLocalGuideData(data)
  }

  const setLocalEmpty = () => {
    const data: Record<GuideRouteName, { time: string; completeSteps: number[] }[]> = {} as Record<
      GuideRouteName,
      { time: string; completeSteps: number[] }[]
    >
    Object.keys(baseGuide).forEach(key => {
      const routeKey = key as GuideRouteName
      data[routeKey] = baseGuide[routeKey].map(guideItem => ({
        time: '',
        completeSteps: []
      }))
    })
    setLocalGuideData(data)
  }
  const getGuideStep = (arr: any, arr2: any) => {
    return arr.filter((item: any) => !arr2?.includes(item.id)).map((item: any) => item.id)
  }
  const initGuideStatus = () => {
    const needOnBoarding = userStore.user?.needOnBoarding
    if (needOnBoarding || needOnBoarding === undefined) {
      // 需要引导
      if (!getLocalGuideData()) {
        setLocalEmpty()
      }
    } else {
      // 不需要新手引导 这里需要自定义，不能全部设置为空：因为新手引导和新功能引导不相同，前者通过接口获取，后者本地配置
      if (!getLocalGuideData()) {
        setLocalAllComplete()
      }
    }
    getCurrentGuideData()
  }

  // 简化的高亮位置设置函数
  const createHighlightPositionSetup = (targetEl: string) => {
    // 创建 resize 事件处理函数
    const handleResize = () => {
      updatePosition(targetEl)
      // 防抖处理
      let timer: any = null
      clearTimeout(timer)
      timer = setTimeout(() => {
        updatePosition(targetEl)
      }, 1000)
    }

    // 创建 MutationObserver
    const observer = new MutationObserver(() => {
      updatePosition(targetEl)
    })

    // 开始监听
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
    window.addEventListener('resize', handleResize)

    // 返回清理函数
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', handleResize)
    }
  }

  const getCurrentGuideData = () => {
    guide.value = getLocalGuideData()
    if (!guide.value) return false
    const localGuideData = guide.value[routeName]
    const baseGuideData = baseGuide[routeName]
    const now = new Date().toJSON().slice(0, 10)
    for (let i = 0; i < localGuideData.length; i++) {
      const steps = baseGuideData[i].steps
      const completeSteps = localGuideData[i].completeSteps
      const time = localGuideData[i].time
      if (time === now && completeSteps?.length === steps.length) {
        return false
      }
      if (!time || (now >= time && steps.length > completeSteps?.length)) {
        guideLevel.value = i
        guideStep.value = getGuideStep(steps, completeSteps)
        return false
      }
    }
  }

  const HighlightBorder = 8
  const targetPosition = ref({ top: 0, left: 0, width: 0, height: 0 })

  const getStyle = () => {
    return {
      top: targetPosition.value.top - HighlightBorder + 'px',
      left: targetPosition.value.left - HighlightBorder + 'px',
      width: targetPosition.value.width + HighlightBorder * 2 + 'px',
      height: targetPosition.value.height + HighlightBorder * 2 + 'px'
    }
  }

  const getContentStyle2 = (top: number, left: number) => {
    // 检查目标位置是否有效
    if (!targetPosition.value.width || !targetPosition.value.height) {
      return {
        top: '50%',
        left: '50%'
      }
    }

    // 使用更精确的响应式计算，避免硬编码基准值
    const scaleFactor = Math.min(window.innerWidth / 1920, 1.2) // 限制最大缩放比例

    // 计算目标元素的中心点
    const targetCenterX = targetPosition.value.left + targetPosition.value.width / 2
    const targetCenterY = targetPosition.value.top + targetPosition.value.height / 2

    // 应用偏移量，确保水平和垂直方向都使用相同的缩放逻辑
    return {
      top: targetCenterY + top * scaleFactor + 'px',
      left: targetCenterX + left * scaleFactor + 'px'
    }
  }

  const getContentStyle = (top: number, left: number) => {
    return {
      top: targetPosition.value.top + targetPosition.value.height / 2 + top + 'px',
      left: targetPosition.value.left + targetPosition.value.width / 2 + left + 'px'
    }
  }

  const updatePosition = (targetEl: string) => {
    const target = document.querySelector(targetEl)
    if (target) {
      const rect = target.getBoundingClientRect()
      targetPosition.value = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      }
    }
  }

  const isLogin = computed(() => userStore.isLogin)

  return {
    isLogin,
    initGuideStatus,
    createHighlightPositionSetup,
    getCurrentGuideData,
    getStyle,
    getContentStyle,
    getContentStyle2,
    updatePosition,
    setLocalGuideData,
    guide,
    guideLevel,
    guideStep
  }
}
